import { BookOpen, Clock, Users } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useTenantPath } from '@/lib/tenant'

interface CourseCardProps {
  id: string
  title: string
  description?: string
  category?: string
  level?: string
  progress?: number
  lessonCount?: number
  enrollmentCount?: number
  totalDuration?: number | null
}

function formatDurationSeconds(seconds: number): string {
  if (seconds < 60) return `${seconds}秒`
  const hours = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  if (hours > 0) return `${hours}時間${mins > 0 ? `${mins}分` : ''}`
  return `${mins}分`
}

export function CourseCard({
  id,
  title,
  description,
  category,
  level,
  progress,
  lessonCount,
  enrollmentCount,
  totalDuration,
}: CourseCardProps) {
  const tp = useTenantPath()

  return (
    <Link to={tp(`/courses/${id}`)}>
      <Card className="hover:shadow-md transition-shadow h-full">
        <CardHeader>
          <CardTitle className="text-base line-clamp-2">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          {description && (
            <p className="text-sm text-muted-foreground line-clamp-3 mb-3">{description}</p>
          )}
          <div className="flex flex-wrap gap-1 mb-3">
            {category && <Badge variant="secondary">{category}</Badge>}
            {level && <Badge variant="outline">{level}</Badge>}
          </div>
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            {lessonCount !== undefined && (
              <div className="flex items-center gap-1">
                <BookOpen className="h-3.5 w-3.5" />
                <span>{lessonCount}レッスン</span>
              </div>
            )}
            {enrollmentCount !== undefined && (
              <div className="flex items-center gap-1">
                <Users className="h-3.5 w-3.5" />
                <span>{enrollmentCount}人</span>
              </div>
            )}
            {totalDuration != null && totalDuration > 0 && (
              <div className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                <span>{formatDurationSeconds(totalDuration)}</span>
              </div>
            )}
          </div>
          {progress !== undefined && (
            <div className="flex items-center gap-2 mt-3">
              <div className="h-2 flex-1 rounded-full bg-gray-200">
                <div
                  className="h-2 rounded-full bg-emerald-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="text-xs text-muted-foreground">{progress}%</span>
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  )
}
